import React, { CSSProperties } from 'react';
import { Checkbox as AkCheckbox } from '@atlaskit/checkbox';
import {
  CheckboxProps as BaseCheckboxProps,
  ComponentTokens,
  ThemeTokens,
} from '@atlaskit/checkbox/types';
import Icon, { IconProps } from '@atlaskit/icon';

import {
  CheckboxClassnameBase,
  CheckboxIconActiveAndCheckedTickColor,
  CheckboxIconActiveBorderColor,
  CheckboxIconActiveBoxColor,
  CheckboxIconCheckedBorderColor,
  CheckboxIconCheckedBoxColor,
  CheckboxIconCheckedTickColor,
  CheckboxIconDefaultBorderColor,
  CheckboxIconDefaultBoxColor,
  CheckboxIconDefaultTickColor,
  CheckboxIconDisabledBorderColor,
  CheckboxIconDisabledBoxColor,
  CheckboxIconDisabledTickColor,
  CheckboxIconFocusBorderColor,
  CheckboxIconHoverBorderColor,
  CheckboxIconHoverBoxColor,
  CheckboxIconHoveredAndCheckedBorderColor,
  CheckboxIconHoveredAndCheckedBoxColor,
  CheckboxIconInvalidBorderColor,
  CheckboxIconPathDefaultTransform,
  CheckboxLabelDefaultAlignItems,
  CheckboxLabelDefaultPaddingX,
  CheckboxLabelDefaultPaddingY,
  ComponentClassnameLabel,
  ComponentClassnameStatic,
  ComponentSizeS as SMALL,
  ComponentStateActive as ACTIVE,
  ComponentStateActiveAndChecked as ACTIVECHECKED,
  ComponentStateChecked as CHECKED,
  ComponentStateDisabled as DISABLED,
  ComponentStateDisabledAndChecked as DISABLEDCHECKED,
  ComponentStateHoveredAndChecked as HOVEREDCHECKED,
  ComponentStateInvalid as INVALID,
  GLOBAL_NAMESPACE_PREFIX,
  TextDefaultColor,
  CheckboxDefaultWidth,
  CheckboxDefaultBorderRadius,
  CheckboxDefaultHeight,
  CheckboxDefaultStrokeWidth,
  CheckboxDefaultViewBoxX,
  CheckboxDefaultViewBoxY,
} from '../../../tokens';

import cx from 'classnames';

import styles from './Checkbox.less';
import { TestId } from '@trello/test-ids';

export const CheckboxClasses = {
  BASE: `${GLOBAL_NAMESPACE_PREFIX}${CheckboxClassnameBase}`,
  LABEL: `${GLOBAL_NAMESPACE_PREFIX}${CheckboxClassnameBase}__${ComponentClassnameLabel}`,
  // For usage with static Checkbox
  STATIC: `${GLOBAL_NAMESPACE_PREFIX}${CheckboxClassnameBase}--${ComponentClassnameStatic}`,
};

const checkboxXDelta = parseInt(CheckboxDefaultStrokeWidth, 10) / 2;
const checkboxYDelta = parseInt(CheckboxDefaultStrokeWidth, 10) / 2;

const checkmarkGlyph = () => (
  <svg
    width={CheckboxDefaultWidth}
    height={CheckboxDefaultHeight}
    viewBox={`0 0 ${CheckboxDefaultViewBoxY} ${CheckboxDefaultViewBoxX}`}
    focusable="false"
    role="presentation"
  >
    <g fillRule="evenodd">
      <rect
        fill="currentColor"
        x={checkboxXDelta}
        y={checkboxYDelta}
        width={CheckboxDefaultWidth}
        height={CheckboxDefaultHeight}
        rx={CheckboxDefaultBorderRadius}
        strokeWidth={CheckboxDefaultStrokeWidth}
      />
      <path
        d="M1.49022 3.21486C1.2407 2.94412 0.818938 2.92692 0.548195 3.17644C0.277453 3.42597 0.260252 3.84773 0.509776 4.11847L2.91785 6.73131C3.2762 7.08204 3.80964 7.08204 4.14076 6.75092C4.18159 6.71082 4.18159 6.71082 4.38359 6.51218C4.57995 6.31903 4.79875 6.1037 5.03438 5.87167C5.70762 5.20868 6.38087 4.54459 7.00931 3.92318L7.0362 3.89659C8.15272 2.79246 9.00025 1.9491 9.47463 1.46815C9.73318 1.20602 9.73029 0.783922 9.46815 0.525367C9.20602 0.266812 8.78392 0.269712 8.52537 0.531843C8.05616 1.00754 7.21125 1.84829 6.09866 2.94854L6.07182 2.97508C5.4441 3.59578 4.77147 4.25926 4.09883 4.92165C3.90522 5.11231 3.72299 5.29168 3.55525 5.4567L1.49022 3.21486Z"
        fill="inherit"
      />
    </g>
  </svg>
);

const indeterminateWidth = parseInt(CheckboxDefaultWidth, 10) / 2;
const indeterminateXDelta = indeterminateWidth / 2;
const checkmarkIndeterminateGlyph = () => (
  <svg
    width={CheckboxDefaultViewBoxX}
    height={CheckboxDefaultViewBoxY}
    viewBox={`0 0 ${CheckboxDefaultViewBoxY} ${CheckboxDefaultViewBoxX}`}
    focusable="false"
    role="presentation"
  >
    <g fillRule="evenodd">
      <rect
        fill="currentColor"
        x={checkboxXDelta}
        y={checkboxYDelta}
        width={CheckboxDefaultWidth}
        height={CheckboxDefaultHeight}
        rx={CheckboxDefaultBorderRadius}
        strokeWidth={CheckboxDefaultStrokeWidth}
      />
      <rect
        fill="inherit"
        x={checkboxXDelta + indeterminateXDelta}
        y={parseInt(CheckboxDefaultHeight, 10) / 2}
        width={indeterminateWidth}
        height={CheckboxDefaultStrokeWidth}
        rx="1"
      />
    </g>
  </svg>
);
class CheckmarkIcon extends React.Component<IconProps, {}> {
  render() {
    return (
      <Icon
        glyph={checkmarkGlyph}
        label=""
        size={SMALL}
        primaryColor="inherit"
        secondaryColor="inherit"
      />
    );
  }
}
class CheckmarkIndeterminateIcon extends React.Component<IconProps, {}> {
  render() {
    return (
      <Icon
        glyph={checkmarkIndeterminateGlyph}
        label=""
        size={SMALL}
        primaryColor="inherit"
        secondaryColor="inherit"
      />
    );
  }
}

type CheckboxLabelType = React.ReactNode | string | null;

const trelloThemeTokens: ComponentTokens = {
  label: {
    textColor: {
      rest: TextDefaultColor,
    },
    spacing: {
      top: CheckboxLabelDefaultPaddingY,
      bottom: CheckboxLabelDefaultPaddingY,
      left: CheckboxLabelDefaultPaddingX, // svg has 4px "padding" already
    },
  },
  icon: {
    borderColor: {
      rest: CheckboxIconDefaultBorderColor,
      hovered: CheckboxIconHoverBorderColor,
      [ACTIVE]: CheckboxIconActiveBorderColor,
      [DISABLED]: CheckboxIconDisabledBorderColor,
      [INVALID]: CheckboxIconInvalidBorderColor,
      [CHECKED]: CheckboxIconCheckedBorderColor,
      [HOVEREDCHECKED]: CheckboxIconHoveredAndCheckedBorderColor,
      focused: CheckboxIconFocusBorderColor,
    },
    boxColor: {
      rest: CheckboxIconDefaultBoxColor,
      hovered: CheckboxIconHoverBoxColor,
      [ACTIVE]: CheckboxIconActiveBoxColor,
      [DISABLED]: CheckboxIconDisabledBoxColor,
      [CHECKED]: CheckboxIconCheckedBoxColor,
      [HOVEREDCHECKED]: CheckboxIconHoveredAndCheckedBoxColor,
    },
    tickColor: {
      rest: CheckboxIconDefaultTickColor,
      [CHECKED]: CheckboxIconCheckedTickColor,
      [ACTIVECHECKED]: CheckboxIconActiveAndCheckedTickColor,
      [DISABLEDCHECKED]: CheckboxIconDisabledTickColor,
    },
  },
};

const trelloTheme = (
  current: (props: { tokens: ComponentTokens; mode: string }) => ThemeTokens,
  props: { tokens: ComponentTokens; mode: string },
) => {
  const baseThemeTokens = current(props);

  // eslint-disable-next-line prefer-object-spread
  return Object.assign({}, baseThemeTokens, trelloThemeTokens);
};

export type CheckboxProps = BaseCheckboxProps & {
  /** A string of classnames to be applied
   *
   * @default ''
   */
  className?: string;
  style?: CSSProperties;
  /**
   * Label: can either be a React node or a string that will be put in a <span>
   * @default null
   */
  label?: CheckboxLabelType;
  testId?: TestId;
};

export class Checkbox extends React.Component<CheckboxProps, {}> {
  private checkboxRef: HTMLInputElement | null | undefined;

  componentDidMount() {
    if (!this.props.testId || !this.checkboxRef) {
      return;
    }

    this.checkboxRef.setAttribute('data-test-id', this.props.testId);
  }

  componentDidUpdate(prevProps: CheckboxProps) {
    if (!this.checkboxRef || this.props.testId === prevProps.testId) {
      return;
    }

    if (!this.props.testId) {
      this.checkboxRef.removeAttribute('data-test-id');
      return;
    }

    this.checkboxRef.setAttribute('data-test-id', this.props.testId);
  }

  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { className, label, onChange, name, testId, ...rest } = this.props;
    const checkboxClassName = cx(
      {
        [CheckboxClasses.BASE]: true,
        [styles[CheckboxClasses.BASE]]: true,
      },
      className,
    );

    let checkboxName = name;
    // for now, if no name is provided, use base classname
    if (!checkboxName) {
      checkboxName = styles[CheckboxClasses.BASE];
    }

    return (
      <div className={checkboxClassName}>
        <AkCheckbox
          theme={trelloTheme}
          name={checkboxName}
          label={label}
          inputRef={(inputRef) => (this.checkboxRef = inputRef)}
          onChange={(e) => {
            // there's some misleading behavior where the blue focus on the border
            // remains on the checkbox after clicking. This blurs the
            // input on click so that the focus ring does not remain
            this.checkboxRef && this.checkboxRef.blur();
            onChange && onChange(e);
          }}
          overrides={{
            Icon: {
              component: CheckmarkIcon,
            },
            IconIndeterminate: {
              component: CheckmarkIndeterminateIcon,
            },
            IconWrapper: {
              cssFn: (defaultStyles) => ({
                ...defaultStyles,
                '& path': {
                  transform: CheckboxIconPathDefaultTransform,
                },
              }),
            },
            Label: {
              cssFn: (defaultStyles) => ({
                ...defaultStyles,
                alignItems: CheckboxLabelDefaultAlignItems,
              }),
            },
          }}
          {...rest}
        />
      </div>
    );
  }
}
